import { Recruiter, Specialization } from '../../types';
import { TFunction, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RECRUITER } from '../../constants/routes';
import TooltipPrompt from '../../UI/tootlip';
import NameImage from '../client/NameImage';
import { Text } from './RecruiterList';
import { ReactComponent as Logo } from '../../icons/logo-ufo.svg';
import styled from 'styled-components';
import { ReactComponent as StarIcon } from '../../icons/star-green-filled.svg';
import CheckboxInput from '../../UI/inputs/CheckboxInput';
import {
  formatRating,
} from '../../utils/formatRecruiters';
import useHirePortTheme from '../../hooks/useHirePortTheme';
import { formatFeeForRecruiterManagement } from '../recruitment_management/RecruiterManagementTableRow';
import useHirePortContext from '../../hooks/useHirePortContext';
import useCurrency from '../../hooks/useCurrency';
import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import Button from '../../UI/buttons/Button';
import { Switch, message } from 'antd';
import { useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { setRecruiterExtensionPermission } from '../../api/jobs';

export const calcRecruiterType = (
  rec: Recruiter,
  t: TFunction<'translation'>,
) => {
  const StyledLogo = styled(Logo)`
    width: 1em;
    height: 1em;
    stroke-width: 1.5;
    & path {
      fill: ${({ theme }) => theme.primaryColor};
    }
  `;

  let CommunityRecruiter = () => {
    const theme = useHirePortTheme();
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
        }}
      >
        <StyledLogo />
        <Text
          style={{
            marginTop: 0,
            marginLeft: '5px',
            marginRight: '10px',
            color: theme.primaryColor,
          }}
        >
          {t('MARKETPLACE')}
        </Text>
      </div>
    );
  };

  return rec.own_recruiter ? (
    <Text>{t('OWN_SUPPLIER')}</Text>
  ) : (
    <CommunityRecruiter />
  );
};

const RecruiterName = ({ recruiter }: { recruiter: Recruiter }) => {
  return <NameImage
    title={recruiter.name}
    image={recruiter.image}
    color={recruiter.own_recruiter
      ? { main: '#7949cd', light: '#f2edfa' }
      : { main: '#29ae5e', light: '#eaf7ef' }}
    description={recruiter.recruiter_type !== 'freelance'
      ? recruiter.company.name
      : recruiter.title}
    managed_recruiter={recruiter.managed_recruiter}
    managing_company_name={recruiter.managing_company_name} />;
};

type RecruiterSelectTableRowProps = {
  recruiter: Recruiter;
  selectedJobSpecialization?: {
    id: string;
    name: string;
  } | null;
  domains: Specialization[];
  setRecruiterIsSelectedField: (rec: Recruiter, is_selected: boolean) => void;
  onEditTargetClick: (recruiter: Recruiter) => void;
  onViewHistoryClick: (assignmentId: string | null | undefined) => void;
  jobId: string | null;
  updateRecruiterPermissionField: (recruiter: Recruiter, canAnswer: boolean) => void;
};

export default function RecruiterSelectTableRow({
  recruiter,
  domains,
  selectedJobSpecialization,
  setRecruiterIsSelectedField,
  onEditTargetClick,
  onViewHistoryClick,
  jobId,
  updateRecruiterPermissionField,
}: RecruiterSelectTableRowProps) {
  const theme = useHirePortTheme();
  const { renderCustom: { recruiterSelection: customRender } } = useHirePortContext();
  const { t } = useTranslation();
  const { curr } = useCurrency(recruiter.fee_currency);
  const user = useAppSelector(state => state.user.user);
  const [loadingPermission, setLoadingPermission] = useState(false);

  const renderTarget = (rec: Recruiter) => {
    if (rec.current_target_type && rec.current_target_value !== null && rec.current_target_value !== undefined) {
      const typeText = rec.current_target_type === 'hires' ? t('TARGET_HIRES', 'Hires') : t('TARGET_INTRODUCTIONS', 'Intros');
      return `${rec.current_target_value} ${typeText}`;
    }
    return t('TARGET_NOT_SET', 'Not Set');
  };

  const handlePermissionToggle = async (checked: boolean) => {
    if (!jobId || !recruiter.id) return;
    setLoadingPermission(true);
    try {
      await setRecruiterExtensionPermission(jobId, recruiter.id, checked);
      updateRecruiterPermissionField(recruiter, checked);
      message.success(t('PERMISSION_UPDATED', 'Permission updated'));
    } catch (error) {
      message.error(t('PERMISSION_UPDATE_FAILED', 'Failed to update permission'));
    } finally {
      setLoadingPermission(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        borderBottom: '1px solid #DAE5EC',
        padding: '6px 0',
      }}
    >
      <CheckboxInput
        style={{ flex: '0 0 5%' }}
        checked={recruiter.is_selected}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setRecruiterIsSelectedField(recruiter, e.target.checked);
        }}
      />
      <div style={{ flex: '0 0 20%', paddingRight: '10px', textAlign: 'left' }}>
        {!customRender?.disableLinkToProfile &&
          <TooltipPrompt title={t('CLICK_TO_SEE_PROFILE')}>
            <Link
              to={`${RECRUITER}/${recruiter.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RecruiterName recruiter={recruiter} />
            </Link>
          </TooltipPrompt>
        }
        {customRender?.disableLinkToProfile &&
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRecruiterIsSelectedField(recruiter, !recruiter.is_selected);
            }}
          >
            <RecruiterName recruiter={recruiter} />
          </Link>
        }
      </div>
      <Text style={{ flex: '0 0 12%', paddingRight: '10px', marginBottom: '0.4rem', textAlign: 'left' }}>
        {recruiter.originating_contract_name || t('NO_CONTRACT', 'Without contract')}
      </Text>
      <Text style={{ flex: '0 0 12%', paddingRight: '10px', marginBottom: '0.4rem', textAlign: 'left' }}>
        {recruiter.fee_type ? recruiter.fee_type.charAt(0).toUpperCase() + recruiter.fee_type.slice(1) : '-'}
      </Text>
      <Text style={{ flex: '0 0 12%', paddingRight: '10px', marginBottom: '0.4rem', textAlign: 'left' }}>
        {formatFeeForRecruiterManagement(
          recruiter.fee_type || '',
          parseFloat(recruiter.fee_description || '') || 0,
          curr,
        ) || '-'}
      </Text>
      <Text style={{ flex: '0 0 14%', paddingRight: '10px', marginBottom: '0.4rem', textAlign: 'left' }}>
        {renderTarget(recruiter)}
        <Button
          type="link"
          icon={<EditOutlined />}
          size="small"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onEditTargetClick(recruiter);
          }}
          aria-label={t('EDIT_TARGET_ARIA', 'Edit Target')}
        />
        <Button
          type="link"
          icon={<HistoryOutlined />}
          size="small"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onViewHistoryClick(recruiter.assignment_id);
          }}
          disabled={!recruiter.assignment_id}
          aria-label={t('VIEW_TARGET_HISTORY_ARIA', 'View Target History')}
        />
      </Text>
      <Text style={{ flex: '0 0 10%', paddingRight: '10px', marginBottom: '0.4rem', textAlign: 'left' }}>
        {t('ADDED_TO_LISTS_PLACEHOLDER', 'Added to 0 lists')}
      </Text>
      <Text
        style={{
          flex: '0 0 10%',
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          marginBottom: '0.4rem',
          textAlign: 'left'
        }}
      >
        {formatRating(recruiter) === '-' ? (
          <div
            style={{
              marginLeft: '0.5em',
            }}
          >
            -
          </div>
        ) : (
          [...Array(formatRating(recruiter))].map((_, i) => (
            <StarIcon
              key={i}
              style={{
                height: '1em',
                width: '1em',
                color: theme.primaryColor,
              }}
            />
          ))
        )}
      </Text>
      <div style={{ flex: '0 0 10%', display: 'flex', alignItems: 'center' }}>
        {(user?.role === 'admin' || user?.role === 'client') && (
          <Switch
            checked={recruiter.can_answer_extension_fields}
            onChange={handlePermissionToggle}
            loading={loadingPermission}
            size="small"
            aria-label={t('TOGGLE_EXTENSION_ANSWER_PERMISSION', 'Toggle permission to answer extension field questions')}
          />
        )}
      </div>
    </div>
  );
}
